import { ref, computed } from '@vue/composition-api'
import _keyBy from 'lodash/keyBy'
import store from '@/store'
import {
  getCatalogs,
  postCatalog,
  postCloneCatalog,
  putCatalog,
  seedCatalogPages,
  getPublicCatalogSettings
} from '@/api'
import { useLoading } from '@coursedog/composables'
import DEFAULT_WEBSITE_SETTINGS from '@/constants/caWebsiteSettings'

const catalogs = ref({})

export default function useCatalogs ({ school = store.getters.school } = {}) {
  const catalogsList = computed(() => catalogs.value
    ? Object.values(catalogs.value)
    : []
  )
  const { startLoading, startLoadingUnless, stopLoading, getIsLoading } = useLoading()

  const isLoading = computed(() => [ 'createCatalog', 'catalogs', 'cloneCatalog' ].some(loadingItemName => getIsLoading(loadingItemName).value))
  const isUpdating = getIsLoading('updateCatalog')

  return {
    isLoading,
    isUpdating,
    catalogs,
    catalogsList,
    fetchCatalogs,
    createCatalog,
    cloneCatalog,
    updateCatalog,
  }

  async function fetchCatalogs () {
    startLoadingUnless('catalogs', !!catalogsList.value.length)
    catalogs.value = _keyBy(await getCatalogs({ school: school }) || [], 'id')
    stopLoading('catalogs')
  }

  async function createCatalog (catalog) {
    startLoading('createCatalog')
    const websiteSettings = await getPublicCatalogSettings({ school }) || DEFAULT_WEBSITE_SETTINGS
    const id = await postCatalog({
      school,
      catalog: { ...catalog, websiteSettings },
    })
    await seedCatalogPages({
      school,
      catalogId: id,
    })
    stopLoading('createCatalog')
  }

  async function updateCatalog (catalog) {
    startLoading('updateCatalog')
    await putCatalog({
      school,
      catalogId: catalog.id,
      catalog,
    })
    catalogs.value[catalog.id] = catalog
    stopLoading('updateCatalog')
  }

  async function cloneCatalog (catalogId, displayName) {
    startLoading('cloneCatalog')
    const catalog = {
      displayName,
    }
    await postCloneCatalog({
      school,
      catalogId,
      catalog,
    })

    stopLoading('cloneCatalog')
  }
}

export function openCatalogUrl (catalogUrl, relativeUrl) {
  // Non-development: use given catalog url
  let url = catalogUrl
  // Development: use local catalog url if available
  if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_DEV_CATALOG_URL) {
    url = process.env.VUE_APP_DEV_CATALOG_URL
    // Cookie that is sherable across localhost domain allows to pass selected catalog information
    // without using env and/or corupting path with query params
    document.cookie = `dev_selected_catalog=${encodeURIComponent(catalogUrl)}; Path=/; Domain=localhost; SameSite=Lax`
  }
  if (url.endsWith('/')) url = url.slice(0, -1)
  if (!url.includes('http')) url = `https://${url}`
  /* eslint-disable security/detect-non-literal-fs-filename*/
  window.open(`${url}/${relativeUrl || ''}`, '_blank', 'noopener')
}
